import { useEffect, useMemo, useState, Fragment } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Search,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/roles";

/**
 * Per-user permission overrides. Lets an admin grant or revoke individual
 * permissions on top of the user's role defaults. "Default" removes the
 * override so the role default applies again.
 */
export default function UserPermissionsDialog({ open, onOpenChange, user }) {
  const [catalog, setCatalog] = useState(null);
  const [state, setState] = useState(null); // {role, role_defaults[], overrides{}, effective[]}
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    Promise.all([
      api.get("/permissions/catalog"),
      api.get(`/permissions/user/${user.id}`),
    ])
      .then(([c, u]) => {
        setCatalog(c.data);
        setState(u.data);
      })
      .catch(() => toast.error("Failed to load permissions"))
      .finally(() => setLoading(false));
  }, [open, user]);

  const roleDefaults = useMemo(
    () => new Set(state?.role_defaults || []),
    [state]
  );

  const grouped = useMemo(() => {
    if (!catalog) return [];
    const f = filter.trim().toLowerCase();
    const byGroup = {};
    catalog.actions.forEach((a) => {
      if (
        f &&
        !a.label.toLowerCase().includes(f) &&
        !a.key.toLowerCase().includes(f) &&
        !a.group.toLowerCase().includes(f)
      )
        return;
      (byGroup[a.group] = byGroup[a.group] || []).push(a);
    });
    return (catalog.groups || Object.keys(byGroup))
      .filter((g) => byGroup[g]?.length)
      .map((g) => [g, byGroup[g]]);
  }, [catalog, filter]);

  const setOverride = async (action, mode) => {
    // mode: "default" | "allow" | "deny"
    setSavingKey(action);
    try {
      if (mode === "default") {
        await api.delete(`/permissions/user/${user.id}/${action}`);
        setState((s) => {
          const ov = { ...s.overrides };
          delete ov[action];
          return { ...s, overrides: ov };
        });
      } else {
        const allowed = mode === "allow";
        await api.post(`/permissions/user/${user.id}`, { action, allowed });
        setState((s) => ({ ...s, overrides: { ...s.overrides, [action]: allowed } }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingKey(null);
    }
  };

  const toggleGroup = (g) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  const overrideCount = Object.keys(state?.overrides || {}).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="user-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-indigo-600" />
            Permissions · {user?.name}
          </DialogTitle>
          <DialogDescription asChild>
            <div>
              Role <span className="inline-flex items-center mx-1 px-2 py-0.5 rounded-full border border-slate-200 text-xs capitalize">{ROLE_LABELS[state?.role] || state?.role}</span>
              defaults apply unless overridden here. {overrideCount > 0 && `${overrideCount} override(s) active.`}
            </div>
          </DialogDescription>
        </DialogHeader>

        {state?.is_super_admin && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            This user is a Super Admin and has unrestricted access. Overrides do not apply.
          </div>
        )}

        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter permissions…"
            className="pl-9 h-9"
            data-testid="user-perm-filter"
          />
        </div>

        <div className="overflow-y-auto flex-1 -mx-1 px-1">
          {loading || !catalog || !state ? (
            <div className="text-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-1">
              {grouped.map(([group, actions]) => {
                const isCollapsed = collapsed[group];
                return (
                  <Fragment key={group}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group)}
                      className="w-full flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-indigo-600 font-bold py-1.5 hover:text-indigo-800"
                    >
                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                      {group}
                      <span className="text-slate-400 font-normal normal-case">({actions.length})</span>
                    </button>
                    {!isCollapsed &&
                      actions.map((a) => {
                        const ov = state.overrides[a.key];
                        const hasOverride = ov !== undefined;
                        const mode = !hasOverride ? "default" : ov ? "allow" : "deny";
                        const def = roleDefaults.has(a.key);
                        const isSaving = savingKey === a.key;
                        return (
                          <div
                            key={a.key}
                            className="flex items-center justify-between gap-3 py-1.5 pl-5 pr-1 border-b border-slate-50"
                            data-testid={`user-perm-row-${a.key}`}
                          >
                            <div className="min-w-0">
                              <div className="text-sm text-slate-700 truncate">{a.label}</div>
                              <div className="text-[10px] text-slate-400 font-mono">
                                {a.key} · role default: {def ? "allowed" : "denied"}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              {isSaving ? (
                                <Loader2 className="w-4 h-4 animate-spin text-indigo-500 mr-1" />
                              ) : (
                                <>
                                  <SegBtn
                                    active={mode === "allow"}
                                    onClick={() => setOverride(a.key, "allow")}
                                    cls="data-[on=true]:bg-emerald-600 data-[on=true]:text-white text-emerald-700"
                                    testid={`user-perm-allow-${a.key}`}
                                  >
                                    Allow
                                  </SegBtn>
                                  <SegBtn
                                    active={mode === "deny"}
                                    onClick={() => setOverride(a.key, "deny")}
                                    cls="data-[on=true]:bg-rose-600 data-[on=true]:text-white text-rose-700"
                                    testid={`user-perm-deny-${a.key}`}
                                  >
                                    Deny
                                  </SegBtn>
                                  {hasOverride && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-1.5 text-slate-400 hover:text-slate-700"
                                      onClick={() => setOverride(a.key, "default")}
                                      title="Reset to role default"
                                      data-testid={`user-perm-reset-${a.key}`}
                                    >
                                      <RotateCcw className="w-3.5 h-3.5" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </Fragment>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SegBtn({ active, onClick, cls, testid, children }) {
  return (
    <button
      type="button"
      data-on={active}
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-2.5 py-1 rounded-md border border-slate-200 transition ${cls} ${
        active ? "" : "bg-white hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
